import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function ChatModal({ isOpen, onClose, richiestaId, userType, userId }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [richiestaData, setRichiestaData] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Scroll automatico verso l'ultimo messaggio
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Carica i messaggi quando si apre il modale
  useEffect(() => {
    if (isOpen && richiestaId) {
      loadMessages();
    }
  }, [isOpen, richiestaId]);

  // Scroll quando cambiano i messaggi
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Polling per aggiornare i messaggi (ogni 5 secondi)
  useEffect(() => {
    if (!isOpen || !richiestaId) return;

    const interval = setInterval(() => {
      loadMessages(true); // true = silent reload (no loading state)
    }, 5000);

    return () => clearInterval(interval);
  }, [isOpen, richiestaId]);

  const parseNoteColloquio = (noteText) => {
    if (!noteText) return [];
    
    console.log('🔍 Parsing note colloquio:', noteText);
    
    // Parse del formato: [DD/MM/YYYY HH:MM] Mittente: messaggio
    const lines = noteText.split('\n').filter(line => line.trim());
    const parsedMessages = [];
    
    lines.forEach((line, index) => {
      console.log(`Riga ${index}:`, line);
      
      // Regex più flessibile per catturare il formato
      const match = line.match(/\[(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2})\]\s*([^:]+):\s*(.+)/);
      
      if (match) {
        const [, timestamp, sender, message] = match;
        console.log(`✅ Match trovato - Timestamp: ${timestamp}, Sender: ${sender}, Message: ${message}`);
        parsedMessages.push({
          created_at: timestamp,
          sender_type: sender.trim().toLowerCase().includes('agenzia') ? 'agenzia' : 'candidato',
          sender_name: sender.trim(),
          message: message.trim()
        });
      } else {
        console.log(`❌ Nessun match per la riga: ${line}`);
        // Se la riga non ha il formato corretto, prova a vedere se è un messaggio vecchio
        // Esempio: "Richiesta inviata il 13/11/2025 alle 19:14"
        if (line.includes('Richiesta inviata')) {
          parsedMessages.push({
            created_at: line.match(/\d{2}\/\d{2}\/\d{4}\s+\w+\s+\d{2}:\d{2}/)?.[0] || 'N/A',
            sender_type: 'system',
            sender_name: 'Sistema',
            message: line
          });
        }
      }
    });
    
    console.log('📦 Messaggi parsati totali:', parsedMessages);
    return parsedMessages;
  };

  const loadMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Caricamento messaggi per richiesta ID:', richiestaId);
      
      const response = await axios.get(
        `${API}/api/v1/richieste-colloquio/${richiestaId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('✅ Risposta ricevuta:', response.data);

      if (response.data) {
        setRichiestaData(response.data);
        console.log('📝 Note colloquio grezze:', response.data.note_colloquio);
        const parsedMessages = parseNoteColloquio(response.data.note_colloquio);
        console.log('💬 Messaggi parsati:', parsedMessages);
        setMessages(parsedMessages);
      }
    } catch (error) {
      console.error('❌ Errore caricamento messaggi:', error);
      console.error('Dettagli errore:', error.response?.data);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;

    setSending(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Formato timestamp: [DD/MM/YYYY HH:MM]
      const now = new Date();
      const timestamp = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      const senderLabel = userType === 'agenzia' ? 'Agenzia' : 'Candidato';
      const newNoteEntry = `[${timestamp}] ${senderLabel}: ${newMessage.trim()}`;
      
      console.log('📤 Invio messaggio:', newNoteEntry);
      
      // Aggiungi al note_colloquio esistente
      const existingNotes = richiestaData?.note_colloquio || '';
      const updatedNotes = existingNotes 
        ? `${existingNotes}\n${newNoteEntry}`
        : newNoteEntry;
      
      console.log('📝 Note aggiornate:', updatedNotes);
      
      const response = await axios.patch(
        `${API}/api/v1/richieste-colloquio/${richiestaId}`,
        {
          note_colloquio: updatedNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('✅ Messaggio inviato, risposta:', response.data);

      if (response.data) {
        // Ricarica i messaggi
        await loadMessages(true);
        setNewMessage('');
      }
    } catch (error) {
      console.error('❌ Errore invio messaggio:', error);
      console.error('Dettagli errore:', error.response?.data);
      alert('Errore nell\'invio del messaggio. Riprova.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;
  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            <h2 className="text-lg font-bold">Chat Colloquio</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages Area */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
          style={{ minHeight: '400px', maxHeight: '500px' }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
              <p>Nessun messaggio ancora</p>
              <p className="text-sm">Inizia la conversazione!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const isMyMessage = msg.sender_type === userType;
              
              return (
                <div
                  key={index}
                  className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                      isMyMessage
                        ? 'bg-blue-500 text-white rounded-br-sm'
                        : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-200'
                    }`}
                  >
                    <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                    <div
                      className={`text-xs mt-1 ${
                        isMyMessage ? 'text-blue-100' : 'text-gray-400'
                      }`}
                    >
                      {msg.created_at}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white rounded-b-xl">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Scrivi un messaggio..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 flex items-center gap-2 ${
                sending || !newMessage.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-lg hover:scale-105'
              }`}
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Invio...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Invia
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChatModal;
